import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { forkJoin, of, interval, Subscription } from 'rxjs';
import { switchMap, takeWhile } from 'rxjs/operators';
import { ActivatedRoute, Router } from '@angular/router';
import { DragDropModule, CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatProgressBarModule } from '@angular/material/progress-bar';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { DialogService } from '../../services/dialog.service';
import { DataService } from '../../services/data.service';
import { LayoutService } from '../../services/layout.service';
import { ProjectGet } from '../../models/project/project-get.model';
import { ProjectBase } from '../../models/project/project-base.model';
import { NewProjectDialogComponent, NewProjectData } from '../../components/new-project-dialog/new-project-dialog.component';
import { DataSourceGet } from '../../models/datasource/datasource-get.model';
import { DataDestinationGet } from '../../models/data-destination/data-destination-get.model';
import { GoalGet } from '../../models/goal/goal-get.model';
import { TaskGet } from '../../models/task/task-get.model';
import { TaskBase } from '../../models/task/task-base.model';
import { NewDataSourceDialogComponent, DataSourceFormData } from '../../components/new-data-source-dialog/new-data-source-dialog.component';
import { SelectGoalDialogComponent, GoalFormData } from '../../components/select-goal-dialog/select-goal-dialog.component';
import { SetDestinationDialogComponent, DestinationFormData } from '../../components/set-destination-dialog/set-destination-dialog.component';
import { StartProjectDialogComponent, StartProjectDialogData } from '../../components/start-project-dialog/start-project-dialog.component';
import { StartProjectInstructionsDialogComponent } from '../../components/start-project-instructions-dialog/start-project-instructions-dialog.component';
import { DatasourceInfoPanelComponent } from '../../components/datasource-info-panel/datasource-info-panel.component';
import { OutputsComponent } from '../outputs/outputs.component';
import { SaveConfirmDialogComponent, SaveConfirmDialogData, SaveConfirmDialogResult } from '../../components/save-confirm-dialog/save-confirm-dialog.component';
import { CleaningStatusService } from '../../services/cleaning-status.service';

// Data models for the workflow
interface DataSource {
  id: string;
  name: string;
  itemCount: number;
  description: string;
  sourceType: string;
}

interface Goal {
  id: string;
  name: string;
  description: string;
}

interface Task {
  id: string;
  name: string;
  taskCount: number;
  description: string;
}

// Audit and metrics interfaces
interface AuditEntry {
  step_index: number;
  task_id: string;
  goal_id: string;
  status: boolean;
  timestamp_start: string;
  timestamp_end: string;
  duration_ms: number;
  inputs: any;
  outputs: any;
  outputs_count: number;
  input_count: number;
  input_rows: number;
  message: string | null;
  error_code: string | null;
  rows_affected: number | null;
  columns_affected: number;
}

interface GoalMetrics {
  goalId: string;
  goalName: string;
  totalDuration: number;
  steps: AuditEntry[];
}

interface TimelineBlock {
  entry: AuditEntry;
  widthPercent: number;
  leftPercent: number;
}

@Component({
  selector: 'app-project-canvas',
  standalone: true,
  imports: [
    CommonModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatProgressBarModule,
    MatTooltipModule,
    MatExpansionModule,
    MatSnackBarModule,
    NewDataSourceDialogComponent,
    SelectGoalDialogComponent,
    SetDestinationDialogComponent,
    DatasourceInfoPanelComponent,
    OutputsComponent,
    DragDropModule
  ],
  templateUrl: './project-canvas.component.html',
  styleUrls: ['./project-canvas-sidebar.component.scss']
})
/**
 * ProjectCanvasComponent manages the main project workflow interface.
 * 
 * This component handles:
 * - Loading and displaying project data (data sources, goals, tasks)
 * - Opening various configuration dialogs
 * - Automatic data refresh after dialog closures to keep UI in sync
 * 
 * Data Refresh Strategy:
 * - All dialogs trigger a refresh when they close to ensure data consistency
 * - Selective refresh options are available for performance optimization
 * - Full reload can be triggered for major changes like new data sources
 */
export class ProjectCanvasComponent implements OnInit, OnDestroy {
  projectId: string | null = null;
  projectName: string = '';
  project: ProjectGet | null = null;
  projectDataSource: DataSourceGet | null = null;
  projectGoals: GoalGet[] = [];
  projectTasks: TaskGet[] = [];
  projectDataDestinations: DataDestinationGet[] = [];
  projectHasOutputs = false;
  loading = false;
  error: string | null = null;
  private pollingSubscription: Subscription | null = null;
  private cleaningStatusSubscription: Subscription | null = null;
  
  // Sidebar mode animation properties
  isAnimating = false;
  isContentHidden = false;
  isEditorVisible = false;
  
  // Sidebar editor properties
  editorMode: 'data-source' | 'datasource-detail' | 'goal' | 'destination' | 'help' | 'outputs' | 'cleaning' | null = null;
  cleaningState: 'in-progress' | 'success' | 'error' | null = null;
  cleaningMessage: string = 'Starting Clean';
  cleaningCurrentStep: number | null = null;
  cleaningTotalSteps: number | null = null;
  cleaningGoalName: string | null = null;
  cleaningColumnName: string | null = null;
  fetchingCleaningResults = false;
  editorData: any = null;
  highlightedItemId: string | null = null;
  editorTitle: string = 'Editor';
  editorSubtitle: string = '';
  
  // Previous editor state for back navigation from outputs
  previousEditorMode: 'data-source' | 'datasource-detail' | 'goal' | 'destination' | 'help' | 'cleaning' | null = null;
  previousEditorData: any = null;
  previousHighlightedItemId: string | null = null;
  previousEditorTitle: string = '';
  previousEditorSubtitle: string = '';
  
  // Cleaning metrics data
  cleaningResultData: any = null;
  metricsResultData: any = null;
  auditEntries: AuditEntry[] = [];
  goalMetrics: GoalMetrics[] = [];
  timelineBlocks: TimelineBlock[] = [];
  selectedBlock: AuditEntry | null = null;
  goalsMap: Map<string, string> = new Map(); // goal_id -> goal_name
  totalDuration = 0;
  
  // Track cleaning results and configuration changes (similar to Anomaly Detection)
  hasCleaningResults = false;
  lastCleanGoalIds: string[] = [];
  lastCleanDestinationIds: string[] = [];
  lastCleanGoalConfigSnapshot: string = ''; // JSON snapshot of goals and tasks for change detection
  
  // Collapsible section state
  isDataSourceSectionExpanded = true;
  isGoalsSectionExpanded = true;
  isDestinationsSectionExpanded = true;
  isOutputsSectionExpanded = false; // Start collapsed
  
  // Pending item tracking (for showing unsaved cards)
  hasPendingDataSource = false;
  
  // Tooltip for first-time users (show when no data source exists)
  showDataSourceTooltip = false;
  // Tooltip to guide users to add their first goal (show after data source is added)
  showGoalTooltip = false;
  hasPendingGoal = false;
  hasPendingDestination = false;
  // When true, after the next destinations refresh, show the 'after-goal' help if no destinations remain
  private redirectToAfterGoalIfNoDestinations = false;
  
  // Track if we're in the middle of a save operation triggered by navigation
  private isSavingBeforeNavigation = false;
  private pendingNavigationAfterSave: (() => void) | null = null;
  
  // ViewChild references to dialog components
  @ViewChild(SelectGoalDialogComponent) goalDialogComponent?: SelectGoalDialogComponent;
  @ViewChild(SetDestinationDialogComponent) destinationDialogComponent?: SetDestinationDialogComponent;
  
  // Workflow data
  dataSource: DataSource | null = null;
  goals: Goal[] = [];
  tasks: Task[] = [];

  constructor(
    private route: ActivatedRoute,
    private router: Router,
    private dialogService: DialogService,
    private dataService: DataService,
    private layoutService: LayoutService,
    private cleaningStatusService: CleaningStatusService,
    private dialog: MatDialog,
    private snackBar: MatSnackBar,
    private location: Location,
    private cdr: ChangeDetectorRef
  ) {}

  goBack(): void {
    this.location.back();
  }

  /**
   * Check if the goal editor has unsaved changes
   */
  private hasGoalUnsavedChanges(): boolean {
    if (this.editorMode === 'goal' && this.goalDialogComponent) {
      // Only check if in configuration stage (not during goal selection)
      if (this.goalDialogComponent.currentStage === 'goal-configuration') {
        return this.goalDialogComponent.hasUnsavedChanges();
      }
    }
    return false;
  }

  /**
   * Prompt user to save changes before navigating away
   * Returns a promise that resolves to true if navigation should proceed
   */
  private async promptSaveChanges(): Promise<boolean> {
    const dialogRef = this.dialog.open(SaveConfirmDialogComponent, {
      width: '450px',
      data: {
        title: 'Unsaved Changes',
        message: 'You have unsaved changes to this goal. Would you like to save them before continuing?',
        saveText: 'Save',
        discardText: 'Discard',
        cancelText: 'Cancel'
      } as SaveConfirmDialogData
    });

    const result: SaveConfirmDialogResult = await dialogRef.afterClosed().toPromise();
    
    if (result === 'save') {
      // User wants to save, trigger save and wait for completion
      if (this.goalDialogComponent) {
        this.isSavingBeforeNavigation = true;
        
        // Create a promise that resolves when the save completes
        const savePromise = new Promise<boolean>((resolve) => {
          // Store the resolve function to be called when save completes
          this.pendingNavigationAfterSave = () => {
            this.isSavingBeforeNavigation = false;
            this.pendingNavigationAfterSave = null;
            resolve(true);
          };
        });
        
        // Trigger the save
        this.goalDialogComponent.onConfirm();
        
        // Wait for save to complete (or timeout after 10 seconds)
        const timeoutPromise = new Promise<boolean>((resolve) => {
          setTimeout(() => {
            if (this.isSavingBeforeNavigation) {
              console.warn('Save operation timed out');
              this.isSavingBeforeNavigation = false;
              this.pendingNavigationAfterSave = null;
              resolve(true); // Allow navigation anyway
            }
          }, 10000);
        });
        
        return await Promise.race([savePromise, timeoutPromise]);
      }
      return true;
    } else if (result === 'discard') {
      // User confirmed discard, proceed with navigation
      return true;
    }
    // If 'cancel' or undefined, stay on current editor
    return false;
  }

  get hasFileOutput(): boolean {
    return this.projectDataDestinations.some(
      d => d.destinationTypeName === 'Output to File' && d.isEnabled
    );
  }

  private async activateSidebarEditor(mode: 'data-source' | 'datasource-detail' | 'goal' | 'destination' | 'outputs', data: any, itemId?: string): Promise<void> {
    // Check for unsaved changes in goal editor before switching
    if (this.hasGoalUnsavedChanges()) {
      const shouldProceed = await this.promptSaveChanges();
      if (!shouldProceed) {
        // User cancelled, clear any pending flags that were set prematurely
        this.hasPendingGoal = false;
        this.hasPendingDestination = false;
        return; // User cancelled, stay on current editor
      }
    }
    
    // Clear pending items only when switching to a different mode or editing existing items
    // Don't clear if we're creating a new item (itemId is undefined and mode matches pending state)
    const isCreatingGoal = mode === 'goal' && !itemId && data?.mode === 'create';
    const isCreatingDestination = mode === 'destination' && !itemId && data?.mode === 'create';
    
    // Set pending flags for new items now that we know the user didn't cancel
    if (isCreatingGoal) {
      this.hasPendingGoal = true;
    } else {
      this.hasPendingGoal = false;
    }
    
    if (isCreatingDestination) {
      this.hasPendingDestination = true;
    } else {
      this.hasPendingDestination = false;
    }
    
    // Reorder updates to avoid changing bound values during Angular's change detection
    // Update internal editor state first, then flip visibility in a microtask so that
    // Angular's change detection sees a single consistent set of values.
    this.isEditorVisible = false;
    // Apply editor content synchronously
    this.editorMode = mode;
    this.editorData = data;
    this.highlightedItemId = itemId || null;

    // Defer making the editor visible until the microtask queue so change detection completes
    Promise.resolve().then(() => {
      this.isEditorVisible = true;
      try { this.cdr.detectChanges(); } catch (e) { /* ignore */ }
    });
  }

  async showHelpContent(type: 'data-source' | 'goal' | 'destination'): Promise<void> {
    // Check for unsaved changes in goal editor before switching to help
    if (this.hasGoalUnsavedChanges()) {
      const shouldProceed = await this.promptSaveChanges();
      if (!shouldProceed) {
        return; // User cancelled, stay on current editor
      }
    }
    
    // Show contextual help after completing a section
    this.isEditorVisible = false;
    setTimeout(() => {
      this.editorMode = 'help';
      this.highlightedItemId = null;
      
      switch (type) {
        case 'data-source':
          this.editorTitle = 'Next Step: Add Goals';
          this.editorSubtitle = 'Now that you have a data source, you can add goals to define what you want to achieve with your data.';
          this.editorData = { helpType: 'after-data-source' };
          this.showGoalTooltip = true;
          break;
        case 'goal':
          this.editorTitle = 'Next Step: Set Destinations';
          this.editorSubtitle = 'Your goals are configured. You can create an additional goal, or set up data destinations to specify where your cleaned data should be sent.';
          this.editorData = { helpType: 'after-goal' };
          this.showGoalTooltip = false;
          break;
        case 'destination':
          this.editorTitle = 'Project Setup Complete';
          this.editorSubtitle = 'Your project is now fully configured. You can view outputs or continue editing your configuration.';
          this.editorData = { helpType: 'after-destination' };
          this.showGoalTooltip = false;
          break;
      }
      
      setTimeout(() => {
        this.isEditorVisible = true;
      }, 50);
    }, 200);
  }

  async closeEditor(): Promise<void> {
    // Check for unsaved changes in goal editor before closing
    if (this.hasGoalUnsavedChanges()) {
      const shouldProceed = await this.promptSaveChanges();
      if (!shouldProceed) {
        return; // User cancelled, keep editor open
      }
    }
    
    this.isEditorVisible = false;
    setTimeout(() => {
      this.editorMode = null;
      this.editorData = null;
      this.highlightedItemId = null;
      this.editorTitle = 'Editor';
      this.editorSubtitle = '';
    }, 200);
  }
  
  onEditorTitleChange(title: string): void {
    this.editorTitle = title;
  }
  
  onEditorSubtitleChange(subtitle: string): void {
    this.editorSubtitle = subtitle;
  }

  handleDataSourceSubmit(result: { data: any }): void {
    console.log('Data source submitted:', result.data);
    
    // Clear pending state
    this.hasPendingDataSource = false;
    
    // Check if an existing data source was selected
    if (result.data.existingDataSourceId) {
      // Update the project with the selected data source ID
      this.updateProjectDataSource(result.data.existingDataSourceId);
    } else {
      // Create a new data source (existing flow)
      this.createDataSource(result.data);
    }
    
    // Show help content for next steps
    this.showHelpContent('data-source');
    this.refreshProjectData({ dataSource: true, fullReload: true });
  }

  handleDataSourceCancel(): void {
    console.log('Data source cancelled');
    this.hasPendingDataSource = false;
    this.showHelpContent('data-source');
  }

  handleDataSourceCreated(event: { dataSourceId: string, dataSourceName: string, hasSchema: boolean }): void {
    console.log('Data source created:', event);
    
    // Update the project data source with the real data
    this.projectDataSource = {
      dataSourceId: event.dataSourceId,
      dataSourceName: event.dataSourceName,
      dataSourceTypeId: 1, // API Listener
      tags: 'api-listener',
      schemaJson: event.hasSchema ? 'pending' : null,
      createdAt: new Date(),
      updatedAt: new Date(),
      projectId: this.projectId || '',
      companyId: ''  // Will be populated from server
    } as DataSourceGet;
    
    // Automatically assign the newly created datasource to the project
    // This makes creating a new datasource the same as selecting an existing one
    this.updateProjectDataSource(event.dataSourceId);
    
    // Clear pending state
    this.hasPendingDataSource = false;
  }

  handleSchemaDetected(event: { dataSourceId: string }): void {
    console.log('Schema detected for data source:', event.dataSourceId);
    
    // Reload the data source to get the updated schema
    this.refreshProjectData({ dataSource: true });
    
    // Expand the goals section now that schema is available
    this.isGoalsSectionExpanded = true;
  }

  handleGoalSubmit(result: { data: GoalFormData; goalTasks: any[] }): void {
    console.log('Goal submitted:', result.data, result.goalTasks);
    // Clear pending state
    this.hasPendingGoal = false;
    
    // If this save was triggered by a navigation attempt, complete the navigation
    if (this.isSavingBeforeNavigation && this.pendingNavigationAfterSave) {
      this.pendingNavigationAfterSave();
      return; // Don't show help content, let the pending navigation complete
    }
    
    // The goal creation/update is already handled inside the dialog component
    // Show help content for next steps
    this.showHelpContent('goal');
    this.refreshProjectData({ goals: true, tasks: true });
    
    // Expand the destinations section now that a goal exists
    this.isDestinationsSectionExpanded = true;
  }

  handleGoalCancel(): void {
    console.log('Goal cancelled');
    
    // The goal dialog component handles unsaved changes warnings internally.
    // We should not check for unsaved changes again here as it would cause a double dialog.
    // Instead, directly close the editor and show help content.
    this.hasPendingGoal = false;
    
    // Close editor and show help content without checking for unsaved changes
    this.isEditorVisible = false;
    setTimeout(() => {
      this.editorMode = 'help';
      this.highlightedItemId = null;
      this.editorTitle = 'Next Step: Set Destinations';
      this.editorSubtitle = 'Your goals are configured. You can create an additional goal, or set up data destinations to specify where your cleaned data should be sent.';
      this.editorData = { helpType: 'after-goal' };
      this.showGoalTooltip = false;
      
      setTimeout(() => {
        this.isEditorVisible = true;
      }, 50);
    }, 200);
  }

  handleDestinationSubmit(result: { data: DestinationFormData }): void {
    console.log('Output submitted:', result.data);
    // Clear pending state
    this.hasPendingDestination = false;
    // The destination creation is already handled inside the dialog component
    
    // Expand the outputs section when a destination is added
    this.isOutputsSectionExpanded = true;
    
    // Show help content for next steps
    this.showHelpContent('destination');
    this.refreshProjectData({ destinations: true });
  }

  handleDestinationCancel(): void {
    console.log('Output cancelled');
    
    // For destinations, check if user has selected an output type
    const destinationDialog = this.destinationDialogComponent;
    
    if (destinationDialog) {
      const hasInput = destinationDialog.formData && destinationDialog.formData.outputType !== '';
      
      // If no input (still on first/initial state), just remove the pending card
      if (!hasInput) {
        this.hasPendingDestination = false;
        this.showHelpContent('destination');
        return;
      }
      
      // If user has made a selection, warn them
      this.dialogService.openConfirmDialog(
        'Discard Changes?',
        'You have unsaved changes. Are you sure you want to discard them?',
        'Discard',
        'Cancel'
      ).subscribe(confirmed => {
        if (confirmed) {
          this.hasPendingDestination = false;
          this.showHelpContent('destination');
        }
        // If not confirmed, stay on the dialog (do nothing)
      });
    } else {
      // Fallback if we can't get the dialog component
      this.hasPendingDestination = false;
      this.showHelpContent('destination');
    }
  }

  openDialogFallback(): void {
    // Temporary method to open the old dialog while we refactor
    this.closeEditor();
    
    if (this.editorMode === 'data-source' && this.editorData) {
      if (this.editorData.mode === 'create') {
        // Re-open as dialog
        const dialogRef = this.dialogService.openDialog(NewDataSourceDialogComponent, {
          width: '800px',
          maxWidth: '90vw',
          data: this.editorData
        });
        
        dialogRef.afterClosed().subscribe(result => {
          this.handleDialogClosure(
            result,
            (result) => {
              this.createDataSource(result.data);
            },
            { dataSource: true, fullReload: true }
          );
        });
      }
    }
  }

  onStartPipeline(): void {
    if (!this.projectId || !this.projectDataSource || !this.projectDataSource.dataSourceId) {
      this.error = 'Project ID or data source is missing';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    if (this.projectDataDestinations.length === 0) {
      this.error = 'At least one output is required to start the workflow';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      return;
    }

    // For API Listener projects, show the guidance dialog instead of directly calling clean
    if (this.projectDataSource.dataSourceTypeId === 1) {
      this.dialogService.openDialog(StartProjectDialogComponent, {
        width: '800px',
        maxWidth: '90vw',
        data: {
          project: this.project,
          dataSource: this.projectDataSource
        } as StartProjectDialogData
      });
      return;
    }

    // Guard: block if this project is already being cleaned
    if (this.cleaningStatusService.isProjectCleaning(this.projectId)) {
      this.snackBar.open(
        `Cleaning is already in progress for "${this.projectName}". Please wait for it to finish.`,
        '×',
        { duration: 5000, horizontalPosition: 'center', verticalPosition: 'top', panelClass: ['error-snackbar'] }
      );
      return;
    }

    // Show confirmation dialog for file-based projects
    this.dialogService.openConfirmDialog(
      'Start Workflow',
      `Start the data cleaning workflow for "${this.projectName}"? Your data will be processed according to the configured goals and sent to the specified destinations.`,
      'Start',
      'Cancel',
      true, // showInstructionsButton
      { project: this.project, dataSource: this.projectDataSource } // instructionsData
    ).subscribe(confirmed => {
      if (confirmed && this.projectId && this.projectDataSource && this.projectDataSource.dataSourceId) {
        this.error = null;

        // Show the cleaning in progress panel
        this.cleaningState = 'in-progress';
        this.cleaningMessage = 'Starting Clean';
        this.cleaningCurrentStep = null;
        this.cleaningTotalSteps = null;
        this.cleaningGoalName = null;
        this.cleaningColumnName = null;
        this.isEditorVisible = false;
        setTimeout(() => {
          this.editorMode = 'cleaning';
          this.editorTitle = 'Running Workflow';
          this.editorSubtitle = '';
          this.editorData = null;
          setTimeout(() => { this.isEditorVisible = true; }, 50);
        }, 200);

        // Notify header indicator
        this.cleaningStatusService.notifyStarted(this.projectId!, this.projectName);

        // First, get the project token
        this.dataService.getProjectToken(this.projectId).subscribe({
          next: (projectToken) => {
            console.log('Project token obtained, starting workflow...');
            
            // Now call cleanData with the project token to get the action token
            this.dataService.cleanData(
              this.projectId!,
              this.projectDataSource!.dataSourceId!,
              projectToken
            ).subscribe({
              next: (actionToken) => {
                console.log('Workflow started, polling for status with token:', actionToken);
                this.cleaningMessage = 'Workflow in Progress';
                this.cleaningCurrentStep = null;
                this.cleaningTotalSteps = null;
                this.cleaningGoalName = null;
                this.cleaningColumnName = null;
                this.cleaningStatusService.notifyProgress(this.projectId!, this.projectName);

                // Poll for status every 3 seconds until success (2) or failure (3)
                this.pollingSubscription?.unsubscribe();
                this.pollingSubscription = interval(3000).pipe(
                  switchMap(() => this.dataService.getLongRunningActionStatus(actionToken)),
                  takeWhile(status => status.status !== 2 && status.status !== 3 && status.status !== 4, true)
                ).subscribe({
                  next: (status) => {
                    if (status.status === 2) {
                      // Success - wait one interval then fetch the result
                      setTimeout(() => {
                      this.fetchingCleaningResults = true;
                      this.cleaningStatusService.notifyProgress(this.projectId!, this.projectName, undefined, undefined, 'Fetching Results');
                      this.dataService.getLongRunningActionResult(actionToken).subscribe({
                        next: (result) => {
                          console.log('Cleaning completed successfully:', result);
                          this.fetchingCleaningResults = false;
                          this.cleaningState = 'success';
                          this.editorTitle = 'Workflow Complete';

                          const responseData = result.cleanResult?.resultJson
                            ? JSON.parse(result.cleanResult.resultJson)
                            : null;
                          this.cleaningStatusService.notifyCompleted(this.projectId!, this.projectName, responseData);
                          
                          // Store the cleaning result data for metrics display
                          this.cleaningResultData = responseData;

                          const metricsData = result.cleanResult?.metricsJson
                            ? JSON.parse(result.cleanResult.metricsJson)
                            : null;
                          
                          // Store the metrics result data for metrics display
                          this.metricsResultData = metricsData;
                          this.processCleaningMetrics();
                          
                          // Mark that we have cleaning results and capture the config snapshot
                          this.hasCleaningResults = true;
                          this.lastCleanGoalIds = this.projectGoals.map(g => g.goalId || '').filter(id => id);
                          this.lastCleanDestinationIds = this.projectDataDestinations.map(d => d.dataDestinationId || '').filter(id => id);
                          
                          // Capture a snapshot of the complete goal configuration including tasks for change detection
                          this.lastCleanGoalConfigSnapshot = JSON.stringify({
                            goals: this.projectGoals.map(g => ({
                              goalId: g.goalId,
                              goalCatalogId: g.goalCatalogId,
                              goalName: g.goalName,
                              description: g.description,
                              isActive: g.isActive,
                              order: g.order,
                              tasks: this.projectTasks
                                .filter(t => t.goalId === g.goalId)
                                .map(t => ({
                                  taskId: t.taskId,
                                  description: t.description,
                                  parameters: t.parameters,
                                  parametersJson: t.parametersJson
                                }))
                            }))
                          });
                          
                          // Reload destinations and outputs to update the View Outputs button status
                          if (this.projectId) {
                            this.loadDataDestinations(this.projectId);
                            this.loadOutputs(this.projectId);
                          }
                          
                          // Don't show snackbar when on project canvas - results are visible in the UI
                        },
                        error: (error) => {
                          console.error('Error fetching cleaning result:', error);
                          this.fetchingCleaningResults = false;
                          this.cleaningState = 'success';
                          this.editorTitle = 'Workflow Complete';
                          this.cleaningStatusService.notifyCompleted(this.projectId!, this.projectName);
                          
                          // Reload destinations and outputs to update the View Outputs button status
                          if (this.projectId) {
                            this.loadDataDestinations(this.projectId);
                            this.loadOutputs(this.projectId);
                          }
                          
                          this.snackBar.open('Cleaning Complete - Could not retrieve results', '×', {
                            horizontalPosition: 'center',
                            verticalPosition: 'top',
                            panelClass: ['error-snackbar']
                          });
                        }
                      });
                      }, 3000);
                    } else if (status.status === 3) {
                      // Failed
                      console.error('Workflow failed:', status.errorMessage);
                      this.error = status.errorMessage || 'Workflow failed';
                      this.cleaningState = 'error';
                      this.cleaningStatusService.notifyFailed(this.projectId!, this.projectName);
                      
                      // Reload destinations and outputs in case partial outputs were created
                      if (this.projectId) {
                        this.loadDataDestinations(this.projectId);
                        this.loadOutputs(this.projectId);
                      }
                    } else if (status.status === 4) {
                      // SaveFailed - engine succeeded but results could not be persisted
                      console.error('Workflow results could not be saved:', status.errorMessage);
                      this.cleaningState = 'error';
                      this.cleaningStatusService.notifyFailed(this.projectId!, this.projectName);
                      
                      // Reload destinations and outputs to check if any files were still created
                      if (this.projectId) {
                        this.loadDataDestinations(this.projectId);
                        this.loadOutputs(this.projectId);
                      }
                      
                      this.snackBar.open('Cleaning completed but the results could not be saved. Please try again.', '×', {
                        horizontalPosition: 'center',
                        verticalPosition: 'top',
                        panelClass: ['error-snackbar']
                      });
                    } else {
                      // Still running — update step progress
                      this.cleaningCurrentStep = status.currentStep ?? null;
                      this.cleaningTotalSteps = status.totalSteps ?? null;
                      this.cleaningGoalName = status.goalName ?? null;
                      
                      // Parse column name from detailsJson
                      let columnName: string | null = null;
                      if (status.detailsJson) {
                        try {
                          const details = JSON.parse(status.detailsJson);
                          // Extract column from various possible locations
                          columnName = details.column?.text || details.column?.source || details.column?.name || details.column || null;
                        } catch (e) {
                          console.warn('Failed to parse detailsJson:', e);
                        }
                      }
                      this.cleaningColumnName = columnName;
                      
                      if (status.currentStep != null && status.totalSteps != null) {
                        this.cleaningStatusService.notifyProgress(this.projectId!, this.projectName, status.currentStep, status.totalSteps, undefined, status.goalName, columnName ?? undefined);
                      }
                    }
                  },
                  error: (error) => {
                    console.error('Error polling for workflow status:', error);
                    this.error = error.message || 'Failed to check workflow status';
                    this.cleaningState = 'error';
                    this.cleaningStatusService.notifyFailed(this.projectId!, this.projectName);
                  }
                });
                
                // Register the subscription with the service so it persists across navigation
                if (this.pollingSubscription && this.projectId) {
                  this.cleaningStatusService.registerPollingSubscription(this.projectId, this.pollingSubscription);
                }
              },
              error: (error) => {
                console.error('Error starting workflow:', error);
                this.error = error.message || 'Failed to start workflow';
                this.cleaningState = 'error';
                this.cleaningStatusService.notifyFailed(this.projectId!, this.projectName);
              }
            });
          },
          error: (error) => {
            console.error('Error getting project token:', error);
            this.error = error.message || 'Failed to get project token';
            this.cleaningState = 'error';
            this.cleaningStatusService.notifyFailed(this.projectId!, this.projectName);
          }
        });
      }
    });
  }

  goToActivity(): void {
    this.router.navigate(['/activity']);
  }

  downloadCleaningResults(): void {
    if (!this.cleaningResultData) {
      return;
    }

    const jsonString = JSON.stringify(this.cleaningResultData, null, 2);
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    
    // Generate filename with project name and timestamp
    const timestamp = new Date().toISOString().replace(/[:.]/g, '-').slice(0, -5);
    const projectName = this.projectName?.replace(/[^a-z0-9]/gi, '_').toLowerCase() || 'project';
    link.download = `${projectName}_cleaning_results_${timestamp}.json`;
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  openStartProjectInstructions(): void {
    this.dialogService.openDialog(StartProjectInstructionsDialogComponent, {
      width: '800px',
      maxWidth: '90vw',
      data: {
        project: this.project,
        dataSource: this.projectDataSource
      }
    });
  }

  /**
   * Checks if the project configuration has changed since the last successful clean
   * Returns true if goals or destinations have been added, removed, reordered, or modified
   */
  hasProjectConfigChanged(): boolean {
    if (!this.hasCleaningResults) {
      return false;
    }

    // Get current goal and destination IDs (preserving order to detect reordering)
    const currentGoalIds = this.projectGoals.map(g => g.goalId || '').filter(id => id);
    const currentDestinationIds = this.projectDataDestinations.map(d => d.dataDestinationId || '').filter(id => id);

    // Check if the ordered arrays are different (detects adds, removes, and reordering)
    const goalsChanged = JSON.stringify(currentGoalIds) !== JSON.stringify(this.lastCleanGoalIds);
    const destinationsChanged = JSON.stringify(currentDestinationIds) !== JSON.stringify(this.lastCleanDestinationIds);

    // Check if goal configurations (properties or tasks) have changed
    const currentGoalConfigSnapshot = JSON.stringify({
      goals: this.projectGoals.map(g => ({
        goalId: g.goalId,
        goalCatalogId: g.goalCatalogId,
        goalName: g.goalName,
        description: g.description,
        isActive: g.isActive,
        order: g.order,
        tasks: this.projectTasks
          .filter(t => t.goalId === g.goalId)
          .map(t => ({
            taskId: t.taskId,
            description: t.description,
            parameters: t.parameters,
            parametersJson: t.parametersJson
          }))
      }))
    });
    const goalConfigChanged = currentGoalConfigSnapshot !== this.lastCleanGoalConfigSnapshot;

    return goalsChanged || destinationsChanged || goalConfigChanged;
  }

  /**
   * Returns to the cleaning results view
   */
  onViewCleaningResults(): void {
    if (!this.hasCleaningResults) {
      return;
    }

    // Switch back to cleaning results view
    this.isEditorVisible = false;
    setTimeout(() => {
      this.editorMode = 'cleaning';
      this.editorTitle = 'Workflow Complete';
      this.editorSubtitle = '';
      this.editorData = null;
      setTimeout(() => { this.isEditorVisible = true; }, 50);
    }, 200);
  }

  onViewOutputs(): void {
    if (this.projectId && this.projectHasOutputs) {
      // Save current editor state before switching to outputs (for back navigation)
      this.previousEditorMode = this.editorMode === 'outputs' ? this.previousEditorMode : this.editorMode;
      this.previousEditorData = this.editorMode === 'outputs' ? this.previousEditorData : this.editorData;
      this.previousHighlightedItemId = this.editorMode === 'outputs' ? this.previousHighlightedItemId : this.highlightedItemId;
      this.previousEditorTitle = this.editorMode === 'outputs' ? this.previousEditorTitle : this.editorTitle;
      this.previousEditorSubtitle = this.editorMode === 'outputs' ? this.previousEditorSubtitle : this.editorSubtitle;
      
      // Instead of navigating, show outputs in the sidebar editor
      this.activateSidebarEditor('outputs', {
        projectId: this.projectId,
        projectName: this.projectName
      }, 'view-outputs');
      
      // Update editor title
      this.editorTitle = 'View Outputs';
      this.editorSubtitle = 'Download and manage your processed data files';
    }
  }

  onBackFromOutputs(): void {
    // Restore previous editor state
    if (this.previousEditorMode) {
      // Handle different editor modes
      if (this.previousEditorMode === 'help' || this.previousEditorMode === 'cleaning') {
        // For help and cleaning modes, restore state directly (not through activateSidebarEditor)
        this.isEditorVisible = false;
        setTimeout(() => {
          this.editorMode = this.previousEditorMode;
          this.editorData = this.previousEditorData;
          this.highlightedItemId = this.previousHighlightedItemId;
          this.editorTitle = this.previousEditorTitle;
          this.editorSubtitle = this.previousEditorSubtitle;
          setTimeout(() => {
            this.isEditorVisible = true;
          }, 50);
        }, 200);
      } else {
        // For other modes, use activateSidebarEditor for proper transition
        this.activateSidebarEditor(
          this.previousEditorMode,
          this.previousEditorData,
          this.previousHighlightedItemId || undefined
        );
        
        // Restore previous titles
        this.editorTitle = this.previousEditorTitle;
        this.editorSubtitle = this.previousEditorSubtitle;
      }
    } else {
      // No previous state, close the editor
      this.closeEditor();
    }
  }

  ngOnInit(): void {
    // Always start in sidebar mode
    this.layoutService.setSidebarMode(true);
    
    // Get project ID from route parameters
    this.projectId = this.route.snapshot.paramMap.get('id');
    
    if (this.projectId) {
      const currentProjectId = this.projectId;
      
      // Subscribe to cleaning status updates for this project
      this.cleaningStatusSubscription = this.cleaningStatusService.statuses$.subscribe(statusesMap => {
        const status = statusesMap.get(currentProjectId);
        if (status) {
          // Update local UI state based on status
          this.cleaningState = status.state;
          this.cleaningMessage = status.message || 'Workflow in Progress';
          this.cleaningCurrentStep = status.currentStep ?? null;
          this.cleaningTotalSteps = status.totalSteps ?? null;
          this.cleaningGoalName = status.goalName ?? null;
          this.cleaningColumnName = status.columnName ?? null;
          
          // If cleaning just completed or failed, reload data
          if (status.state === 'success' || status.state === 'error') {
            if (status.state === 'success') {
              this.editorTitle = 'Workflow Complete';
              // Reload destinations and outputs to update the View Outputs button status
              this.loadDataDestinations(currentProjectId);
              this.loadOutputs(currentProjectId);
            } else {
              this.editorTitle = 'Workflow Failed';
            }
          }
        }
      });
      
      // Check if there's an active cleaning operation for this project
      if (this.cleaningStatusService.isProjectCleaning(this.projectId)) {
        // Restore the cleaning UI state
        this.cleaningState = 'in-progress';
        this.cleaningMessage = 'Workflow in Progress';
        this.editorMode = 'cleaning';
        this.editorTitle = 'Running Workflow';
        this.editorSubtitle = '';
        this.editorData = null;
        this.isEditorVisible = true;
        
        // Reconnect to the existing polling subscription
        const existingSubscription = this.cleaningStatusService.getPollingSubscription(this.projectId);
        if (existingSubscription) {
          this.pollingSubscription = existingSubscription;
        }
      } else {
        // Show initial help content about how the Project Canvas works
        this.editorMode = 'help';
        this.editorTitle = 'Cleaning Workflow Canvas';
        this.editorSubtitle = 'Your workspace for building and managing your data cleaning workflows';
        this.editorData = { helpType: 'initial-canvas-help' };
        this.isEditorVisible = true;
      }
      
      this.loadProject(this.projectId);
    } else {
      this.error = 'No project ID provided in route';
      this.snackBar.open(this.error, '×', {
        duration: 5000,
        horizontalPosition: 'center',
        verticalPosition: 'top',
        panelClass: ['error-snackbar']
      });
      console.error('Project ID is required but not found in route parameters');
    }
  }

  ngOnDestroy(): void {
    // Reset sidebar mode when leaving the component
    this.layoutService.setSidebarMode(false);
    
    // Unsubscribe from cleaning status updates
    this.cleaningStatusSubscription?.unsubscribe();
    this.cleaningStatusSubscription = null;
    
    // Only unsubscribe from polling if there's NO active cleaning operation
    // If there is an active clean, let it continue - it's managed by the CleaningStatusService
    if (this.pollingSubscription && this.projectId && !this.cleaningStatusService.isProjectCleaning(this.projectId)) {
      this.pollingSubscription.unsubscribe();
    }
    
    // Clear the local reference regardless (the service manages the subscription now)
    this.pollingSubscription = null;
  }

  toggleDataSourceSection(): void {
    this.isDataSourceSectionExpanded = !this.isDataSourceSectionExpanded;
  }

  toggleGoalsSection(): void {
    this.isGoalsSectionExpanded = !this.isGoalsSectionExpanded;
  }

  toggleDestinationsSection(): void {
    this.isDestinationsSectionExpanded = !this.isDestinationsSectionExpanded;
  }

  toggleOutputsSection(): void {
    this.isOutputsSectionExpanded = !this.isOutputsSectionExpanded;
  }

  /**
   * Refreshes all project data - used after dialog closures to ensure UI is up to date
   * @param options Optional configuration for what to refresh
   */
  refreshProjectData(options?: { 
    dataSource?: boolean; 
    goals?: boolean; 
    tasks?: boolean;
    destinations?: boolean;
    fullReload?: boolean 
  }): void {
    if (!this.projectId) return;
    
    const refreshOptions = {
      dataSource: true,
      goals: true,
      tasks: true,
      destinations: true,
      fullReload: false,
      ...options
    };

    console.log('Refreshing project data after dialog closure', refreshOptions);
    
    if (refreshOptions.fullReload) {
      // Full reload - reloads everything including project metadata
      this.loadProject(this.projectId);
    } else {
      // Selective refresh - only refresh specific components
      this.loading = true;
      
      let refreshTasks = 0;
      let completedTasks = 0;
      
      const checkRefreshComplete = () => {
        completedTasks++;
        if (completedTasks === refreshTasks) {
          this.loading = false;
          this.initializeWorkflowData();

          // If a destination was just deleted and there are no destinations remaining,
          // show the post-goal help screen instead of the post-destination screen.
          if (this.redirectToAfterGoalIfNoDestinations) {
            this.redirectToAfterGoalIfNoDestinations = false;
            if (!this.projectDataDestinations || this.projectDataDestinations.length === 0) {
              // Use the existing helper to show the goal-related help content
              this.showHelpContent('goal');
            }
          }
        }
      };

      // Refresh data source if requested
      if (refreshOptions.dataSource && this.project?.dataSourceId) {
        refreshTasks++;
        const dataSourceId = this.project.dataSourceId;
        if (dataSourceId) {
          this.loadDataSource(dataSourceId, checkRefreshComplete);
        }
      }

      // Refresh goals if requested
      if (refreshOptions.goals) {
        refreshTasks++;
        this.loadGoals(this.projectId, checkRefreshComplete);
      }

      // Refresh data destinations if requested
      if (refreshOptions.destinations) {
        refreshTasks++;
        this.loadDataDestinations(this.projectId, checkRefreshComplete);
      }

      // If no specific refresh tasks, just reload everything
      if (refreshTasks === 0) {
        this.loadProject(this.projectId);
      }
    }
  }

  /**
   * Handles dialog closure with consistent refresh logic
   * @param result The result from the dialog
   * @param successCallback Optional callback for successful operations
   * @param options Optional refresh configuration
   */
  private handleDialogClosure(
    result: any, 
    successCallback?: (result: any) => void,
    options?: { dataSource?: boolean; goals?: boolean; tasks?: boolean; destinations?: boolean; fullReload?: boolean }
  ): void {
    if (result && (result.success || result.data)) {
      console.log('Dialog completed successfully:', result);
      if (successCallback) {
        successCallback(result);
      }
    }
    
    // Always refresh after dialog closure to ensure UI is up to date
    this.refreshProjectData(options);
  }

  /**
   * Enables automatic refresh detection for development/debugging
   * This can be useful for detecting when data might have changed outside the UI
   */
  enableAutoRefresh(intervalMs: number = 30000): void {
    console.log(`Auto-refresh enabled with ${intervalMs}ms interval`);
    setInterval(() => {
      if (this.projectId && !this.loading) {
        console.log('Auto-refreshing project data');
        this.refreshProjectData({ fullReload: false });
      }
    }, intervalMs);
  }

  /**
   * Manual refresh method that can be called from templates or other components
   * This is useful for refresh buttons or external triggers
   */
  onManualRefresh(): void {
    console.log('Manual refresh triggered by user');
    this.refreshProjectData({ fullReload: true });
  }

  loadProject(projectId: string): void {
    this.loading = true;
    this.error = null;
    
    this.dataService.getProject(projectId).subscribe({
      next: (project) => {
        this.project = project;
        this.projectName = project.projectName;
        
        // Load project-related data
        this.loadProjectData();
      },
      error: (error) => {
        this.error = error.message || 'Failed to load project';
        this.snackBar.open(error.message || 'Failed to load project', '×', {
          duration: 5000,
          horizontalPosition: 'center',
          verticalPosition: 'top',
          panelClass: ['error-snackbar']
        });
        this.loading = false;
        console.error('Error loading project:', error);
      }
    });
  }

  private loadProjectData(): void {
    if (!this.project || !this.projectId) return;

    let loadingTasks = 0;
    let completedTasks = 0;

    const checkLoadingComplete = () => {
      completedTasks++;
      if (completedTasks === loadingTasks) {
        this.loading = false;
        this.initializeWorkflowData();
      }
    };

    // Load data source if available
    if (this.project.dataSourceId) {
      loadingTasks++;
      this.loadDataSource(this.project.dataSourceId, checkLoadingComplete);
    }

    // Load goals for the project
    loadingTasks++;
    this.loadGoals(this.projectId, checkLoadingComplete);

    // Load data destinations for the project
    loadingTasks++;
    this.loadDataDestinations(this.projectId, checkLoadingComplete);

    // Check if project has outputs
    loadingTasks++;
    this.loadOutputs(this.projectId, checkLoadingComplete);

    // If no additional data to load, complete immediately
    if (loadingTasks === 0) {
      this.loading = false;
      this.initializeWorkflowData();
    }
  }

  loadDataSource(dataSourceId: string, onComplete?: () => void): void {
    this.dataService.getDataSource(dataSourceId).subscribe({
      next: (dataSource) => {
        this.projectDataSource = dataSource;
        if (onComplete) onComplete();
      },
      error: (error) => {
        console.error('Error loading data source:', error);
        // Don't set error state for data source failure, just log it
        if (onComplete) onComplete();
      }
    });
  }

  loadGoals(projectId: string, onComplete?: () => void): void {
    this.dataService.getGoalList(projectId).subscribe({
      next: (goals) => {
        // Sort by order if available, otherwise fall back to createdAt ascending
        this.projectGoals = goals.sort(
          (a, b) => {
            if (a.order != null && b.order != null) {
              return a.order - b.order;
            }
            const dateA = a.createdAt ? new Date(a.createdAt) : new Date(0);
            const dateB = b.createdAt ? new Date(b.createdAt) : new Date(0);
            return dateA.getTime() - dateB.getTime();
          }
        );
        
        // Load tasks for each goal
        if (goals.length > 0) {
          this.loadTasksForGoals(goals, onComplete);
        } else {
          // No goals, so no tasks to load
          this.projectTasks = [];
          if (onComplete) onComplete();
        }
      },
      error: (error) => {
        console.error('Error loading goals:', error);
        // Don't set error state for goals failure, just log it
        if (onComplete) onComplete();
      }
    });
  }

  loadTasksForGoals(goals: GoalGet[], onComplete?: () => void): void {
    if (goals.length === 0) {
      this.projectTasks = [];
      if (onComplete) onComplete();
      return;
    }

    let tasksLoaded = 0;
    const allTasks: TaskGet[] = [];

    const checkTasksComplete = () => {
      tasksLoaded++;
      if (tasksLoaded === goals.length) {
        this.projectTasks = allTasks;
        if (onComplete) onComplete();
      }
    };

    // Load tasks for each goal
    goals.forEach(goal => {
      if (goal.goalId) {
        this.dataService.getTaskList(goal.goalId).subscribe({
          next: (tasks) => {
            // Parse parameters for each task
            const parsedTasks = tasks.map(task => ({
              ...task,
              parameters: typeof task.parameters === 'string' ? JSON.parse(task.parameters) : task.parameters
            }));
            allTasks.push(...parsedTasks);
            checkTasksComplete();
          },
          error: (error) => {
            console.error(`Error loading tasks for goal ${goal.goalId}:`, error);
            // Continue with other goals even if one fails
            checkTasksComplete();
          }
        });
      } else {
        // Goal has no ID, skip it
        checkTasksComplete();
      }
    });
  }

  loadDataDestinations(projectId: string, onComplete?: () => void): void {
    this.dataService.getDataDestinations(projectId).subscribe({
      next: (destinations) => {
        this.projectDataDestinations = destinations;
        console.log('Data destinations loaded:', destinations);
        
        // Auto-expand outputs section if destinations exist
        if (destinations.length > 0) {
          this.isOutputsSectionExpanded = true;
        }
        
        if (onComplete) onComplete();
      },
      error: (error) => {
        console.error('Error loading data destinations:', error);
        // Don't set error state for destinations failure, just log it
        this.projectDataDestinations = [];
        if (onComplete) onComplete();
      }
    });
  }

  loadOutputs(projectId: string, onComplete?: () => void): void {
    this.dataService.getOutputList(projectId).subscribe({
      next: (outputs) => {
        this.projectHasOutputs = outputs.length > 0;
        console.log('Outputs check:', this.projectHasOutputs);
        if (onComplete) onComplete();
      },
      error: (error) => {
        console.error('Error loading outputs:', error);
        this.projectHasOutputs = false;
        if (onComplete) onComplete();
      }
    });
  }

  initializeWorkflowData(): void {
    // Initialize workflow data based on the loaded project
    // If we have a project data source, use it to populate the workflow
    if (this.projectDataSource) {
      this.dataSource = {
        id: this.projectDataSource.dataSourceId || '',
        name: this.projectDataSource.dataSourceName || 'Unknown Data Source',
        sourceType: this.getDataSourceTypeLabel(this.projectDataSource.dataSourceTypeId),
        itemCount: 1, // Default to 1 item
        description: 'Data source loaded from project'
      };
      // Hide tooltip if data source exists
      this.showDataSourceTooltip = false;
    } else {
      // Start with empty state if no data source is available
      this.dataSource = null;
      // Show tooltip for first-time users when no data source exists
      this.showDataSourceTooltip = true;
    }
    
    // Initialize goals from project goals
    if (this.projectGoals && this.projectGoals.length > 0) {
      this.goals = this.projectGoals.map(projectGoal => ({
        id: projectGoal.goalId || '',
        name: projectGoal.goalName || 'Unknown Goal',
        description: projectGoal.description || 'Goal loaded from project'
      }));
    } else {
      this.goals = [];
    }
    
    // Initialize tasks from project tasks
    if (this.projectTasks && this.projectTasks.length > 0) {
      this.tasks = this.projectTasks.map(projectTask => ({
        id: projectTask.id || '',
        name: projectTask.taskId || 'Unknown Task',
        taskCount: 1, // Default to 1 task
        description: projectTask.description || 'Task loaded from project'
      }));
    } else {
      this.tasks = [];
    }
    
    // Note: Data destinations are now handled directly via projectDataDestinations array
    // No need to initialize a single 'output' property
    
    // Auto-manage section expansion based on workflow progress
    this.isDataSourceSectionExpanded = !this.projectDataSource; // Collapse when data source exists
    this.isGoalsSectionExpanded = this.hasValidSchema(); // Open when schema is present
    this.isDestinationsSectionExpanded = this.projectGoals.length > 0; // Open when goals are present
    this.isOutputsSectionExpanded = this.projectDataDestinations.length > 0; // Open when destinations are present

    // If a data source exists but no goals have been added yet, automatically launch goal selection
    if (this.projectDataSource && this.projectGoals.length === 0 && this.editorMode === 'help' &&
        (!this.editorData || this.editorData.helpType === 'initial-canvas-help')) {
      // Launch goal selection directly instead of showing help content
      this.addGoal();
    }
  }

  private getDataSourceTypeLabel(dataSourceTypeId: number): string {
    // Map data source type IDs to labels
    switch (dataSourceTypeId) {
      case 1: return 'API Listener';
      default: return 'Unknown';
    }
  }

  isApiListenerWaitingForData(): boolean {
    return this.projectDataSource?.dataSourceTypeId === 1 && 
           (!this.projectDataSource.schemaJson || this.projectDataSource.schemaJson.trim() === '' || this.projectDataSource.schemaJson === 'null');
  }

  hasValidSchema(): boolean {
    return !!(this.projectDataSource?.schemaJson && 
             this.projectDataSource.schemaJson.trim() !== '' && 
             this.projectDataSource.schemaJson !== 'null');
  }

  getSchemaDetails(): { columnCount: number; rowCount?: number; columns?: string[] } {
    if (!this.hasValidSchema()) {
      return { columnCount: 0 };
    }

    try {
      const schema = JSON.parse(this.projectDataSource!.schemaJson);
      
      // Handle new schema format: [{"column":"ProductID","atomic_dtype":"numeric","soft_dtype":"numeric","stats":{...}}, ...]
      if (Array.isArray(schema) && schema.length > 0 && schema[0].column) {
        const columns = schema.map((col: any) => col.column);
        return {
          columnCount: columns.length,
          rowCount: schema[0].stats?.n_rows,
          columns: columns
        };
      }
      
      // Handle old schema format: [{"ColumnName":"user_id","DataType":"currency"}, ...]
      if (Array.isArray(schema) && schema.length > 0 && schema[0].ColumnName) {
        const columns = schema.map((col: any) => col.ColumnName);
        return {
          columnCount: columns.length,
          columns: columns
        };
      }
      
      // Handle other potential schema formats
      if (schema.columns && Array.isArray(schema.columns)) {
        // Format: { columns: [...] }
        return {
          columnCount: schema.columns.length,
          rowCount: schema.rowCount || schema.rows,
          columns: schema.columns.map((col: any) => typeof col === 'string' ? col : col.name || col.columnName)
        };
      } else if (schema.properties && typeof schema.properties === 'object') {
        // JSON Schema format: { properties: { col1: {}, col2: {} } }
        const columns = Object.keys(schema.properties);
        return {
          columnCount: columns.length,
          columns: columns
        };
      } else if (Array.isArray(schema) && schema.length > 0 && typeof schema[0] === 'object' && !schema[0].ColumnName) {
        // Format: [{ column1: value }, { column2: value }] - sample data
        const columns = Object.keys(schema[0]);
        return {
          columnCount: columns.length,
          rowCount: schema.length,
          columns: columns
        };
      }
      
      return { columnCount: 0 };
    } catch (error) {
      console.warn('Failed to parse schema JSON:', error);
      return { columnCount: 0 };
    }
  }

  getSchemaDisplayText(): string {
    if (!this.hasValidSchema()) {
      return this.projectDataSource?.dataSourceTypeId === 1 ? 'API Listener is waiting for data' : 'No schema available';
    }

    const details = this.getSchemaDetails();
    if (details.columnCount === 0) {
      return 'Schema available';
    }

    let text = `${details.columnCount} column${details.columnCount !== 1 ? 's' : ''}`;
    if (details.rowCount && details.rowCount > 0) {
      text += `, ${details.rowCount} row${details.rowCount !== 1 ? 's' : ''}`;
    }
    
    return text;
  }

  getSchemaDataForDialog(): { columns: { name: string; dataType: string }[] } | null {
    if (!this.hasValidSchema()) {
      return null;
    }

    try {
      const schema = JSON.parse(this.projectDataSource!.schemaJson);
      
      // Handle new schema format: [{"column":"ProductID","atomic_dtype":"numeric","soft_dtype":"numeric","stats":{...}}, ...]
      if (Array.isArray(schema) && schema.length > 0 && schema[0].column) {
        return {
          columns: schema.map((col: any) => ({
            name: col.column,
            dataType: col.atomic_dtype || 'string'
          }))
        };
      }
      
      // Handle old schema format: [{"ColumnName":"user_id","DataType":"currency"}, ...]
      if (Array.isArray(schema) && schema.length > 0 && schema[0].ColumnName) {
        return {
          columns: schema.map((col: any) => ({
            name: col.ColumnName,
            dataType: col.DataType || 'string'
          }))
        };
      }
      
      // Handle other potential schema formats and convert to consistent format
      if (schema.columns && Array.isArray(schema.columns)) {
        return {
          columns: schema.columns.map((col: any) => ({
            name: typeof col === 'string' ? col : col.name || col.columnName,
            dataType: typeof col === 'string' ? 'string' : col.dataType || col.type || 'string'
          }))
        };
      } else if (schema.properties && typeof schema.properties === 'object') {
        // JSON Schema format: { properties: { col1: { type: "string" }, col2: { type: "number" } } }
        return {
          columns: Object.keys(schema.properties).map(key => ({
            name: key,
            dataType: schema.properties[key]?.type || 'string'
          }))
        };
      } else if (Array.isArray(schema) && schema.length > 0 && typeof schema[0] === 'object' && !schema[0].ColumnName) {
        // Format: [{ column1: value }, { column2: value }] - sample data, infer types
        const firstRow = schema[0];
        return {
          columns: Object.keys(firstRow).map(key => ({
            name: key,
            dataType: this.inferDataType(firstRow[key])
          }))
        };
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to parse schema JSON for dialog:', error);
      return null;
    }
  }

  private inferDataType(value: any): string {
    if (typeof value === 'number') {
      return Number.isInteger(value) ? 'integer' : 'number';
    } else if (typeof value === 'boolean') {
      return 'boolean';
    } else if (value instanceof Date || (typeof value === 'string' && !isNaN(Date.parse(value)))) {
      return 'date';
    } else {
      return 'string';
    }
  }

  /**
   * Get the full schema array for suggest-columns endpoint
   */
  getFullSchemaForDialog(): any[] | null {
    if (!this.hasValidSchema()) {
      return null;
    }

    try {
      const schema = JSON.parse(this.projectDataSource!.schemaJson);
      
      // Return the schema array if it's in the new format with full structure
      if (Array.isArray(schema)) {
        return schema;
      }
      
      return null;
    } catch (error) {
      console.warn('Failed to parse schema JSON for full schema:', error);
      return null;
    }
  }

  /**
   * Get existing goal names for duplicate validation
   * @param excludeGoalId Optional goal ID to exclude (when editing a goal)
   * @returns Array of existing goal names
   */
  private getExistingGoalNames(excludeGoalId?: string): string[] {
    return this.projectGoals
      .filter(goal => !excludeGoalId || goal.goalId !== excludeGoalId)
      .map(goal => goal.goalName)
      .filter(name => !!name) as string[];
  }

  initializeSampleData(): void {
    // This method is available for testing completed project states
    // Currently commented out so all projects start as new/empty
    /*
    this.dataSource = {
      id: '1',
      name: 'Customer Data',
      itemCount: 2,
      description: 'Click + to set goals for the selected data sources'
    };
    
    this.goals = [
      {
        id: '1',
        name: 'Clean Email',
        description: 'Click on the hamburger icon to change goal'
      }
    ];
    
    this.tasks = [
      {
        id: '1',
        name: 'Data Cleaning',
        taskCount: 2,
        description: 'Click on the hamburger icon to configure tasks'
      }
    ];
    */
  }

  onBackToProjects(): void {
    this.router.navigate(['/activity']);
  }

  // Data Source methods
  addDataSource(): void {
    this.hasPendingDataSource = true;
    this.activateSidebarEditor('data-source', {
      title: 'New Data Source',
      projectId: this.projectId,
      mode: 'create'
    });
  }

  createDataSource(data: DataSourceFormData): void {
    this.dataSource = {
      id: Date.now().toString(),
      name: data.name || `New ${data.connectionType} Source`,
      sourceType: this.getSourceTypeLabel(data.connectionType),
      itemCount: 1, // Default to 1 item for new data sources
      description: data.description || 'Click + to set goals for the selected data sources'
    };

    console.log('Data source created for project canvas:', this.dataSource);
  }

  updateProjectDataSource(dataSourceId: string): void {
    if (!this.project || !this.projectId) {
      console.error('Cannot update project: Project data is not available');
      return;
    }

    // Update the project with the selected data source ID
    const updatedProject: ProjectBase = {
      projectId: this.projectId,
      projectName: this.project.projectName,
      catalogId: this.project.catalogId,
      description: this.project.description
    };

    // Update the local project object to include the data source ID
    this.project = {
      ...this.project,
      dataSourceId: dataSourceId
    };

    this.dataService.updateProject(updatedProject).subscribe({
      next: (success) => {
        if (success) {
          console.log('Project updated with data source ID:', dataSourceId);
        } else {
          console.error('Failed to update project with data source');
        }
      },
      error: (error) => {
        console.error('Error updating project with data source:', error);
      }
    });
  }

  private getSourceTypeLabel(connectionType: string): string {
    switch (connectionType) {
      case 'excel': return 'File (Excel)';
      case 'csv': return 'File (CSV)';
      case 'parquet': return 'File (Parquet)';
      case 'json': return 'File (JSON)';
      case 'api-listener': return 'API Listener';
      case 'api-retriever': return 'API Retriever';
      default: return 'Unknown';
    }
  }

  get projectTitleFontSize(): string {
    const len = (this.projectName || 'Project Canvas').length;
    if (len <= 15) return '28px';
    if (len <= 22) return '22px';
    if (len <= 30) return '17px';
    if (len <= 40) return '15px';
    return '13px';
  }

  editProject(): void {
    if (!this.project || !this.projectId) return;

    const dialogRef = this.dialogService.openDialog(NewProjectDialogComponent, {
      width: '600px',
      data: {
        name: this.project.projectName,
        description: this.project.description || '',
        tags: '',
        isEdit: true,
        projectId: this.projectId
      } as NewProjectData
    });

    dialogRef.afterClosed().subscribe(result => {
      if (result && result.data) {
        const projectData: ProjectBase = {
          projectId: this.projectId!,
          projectName: result.data.name,
          description: result.data.description || ''
        };
        this.dataService.updateProject(projectData).subscribe({
          next: (success: boolean) => {
            if (success) {
              this.refreshProjectData({ fullReload: true });
            }
          },
          error: (error) => {
            console.error('Error updating project:', error);
          }
        });
      }
    });
  }

  editDataSource(): void {
    if (!this.projectDataSource) {
      console.error('No data source to edit');
      return;
    }

    // Show datasource details instead of edit dialog
    this.activateSidebarEditor('datasource-detail', {
      title: this.projectDataSource.dataSourceName,
      dataSource: this.projectDataSource
    }, this.projectDataSource.dataSourceId?.toString());

    // Update editor title/subtitle
    this.editorTitle = this.projectDataSource.dataSourceName;
    this.editorSubtitle = 'Data Source';
  }

  private getConnectionTypeFromDataSourceType(dataSourceTypeId: number): string {
    // Map data source type ID to connection type string
    switch (dataSourceTypeId) {
      case 1: return 'api-listener';
      case 2: return 'api-retriever';
      // Add other mappings as needed
      default: return 'csv'; // Default fallback
    }
  }

  private getFinalStageForConnectionType(connectionType: string): 'connection-type' | 'file-upload' | 'api-setup' | 'api-listening' {
    // Return the final stage for each connection type
    switch (connectionType) {
      case 'excel':
      case 'csv':
      case 'parquet':
      case 'json':
        return 'file-upload';
      case 'api-listener':
        return 'api-listening';
      case 'api-retriever':
        return 'api-setup';
      default:
        return 'connection-type';
    }
  }

  // Goal methods
  addGoal(): void {
    if (!this.projectId) {
      console.error('Cannot add goal: Project ID is not available');
      return;
    }

    // Note: hasPendingGoal is set inside activateSidebarEditor after unsaved changes check
    this.activateSidebarEditor('goal', {
      title: 'Select Goal',
      projectId: this.projectId,
      mode: 'create',
      initialOrder: this.projectGoals.length + 1,
      schemaData: this.getSchemaDataForDialog(),
      fullSchema: this.getFullSchemaForDialog(),
      existingGoalNames: this.getExistingGoalNames()
    });
  }

  editGoal(goal: GoalGet): void {
    console.log('=== editGoal called ===');
    console.log('goal object:', goal);
    console.log('goal.isActive:', goal.isActive, 'type:', typeof goal.isActive);
    
    if (!this.projectId) {
      console.error('Cannot edit goal: Project ID is not available');
      return;
    }

    // Get tasks for this goal
    const tasksForGoal = this.getTasksForGoal(goal);
    console.log('Found tasks for goal:', tasksForGoal);
    
    // Create a goal object with tasks included
    const goalWithTasks = {
      ...goal,
      tasks: tasksForGoal.map(task => ({
        taskId: task.taskId,
        description: task.description,
        parameters: task.parameters,
        parametersJson: task.parametersJson,
        goalId: task.goalId,
        createdAt: task.createdAt
      }))
    };
    
    console.log('goalWithTasks:', goalWithTasks);
    console.log('goalWithTasks.isActive:', goalWithTasks.isActive);

    this.activateSidebarEditor('goal', {
      title: 'Edit Goal',
      projectId: this.projectId,
      mode: 'edit',
      goalData: goalWithTasks,
      schemaData: this.getSchemaDataForDialog(),
      fullSchema: this.getFullSchemaForDialog(),
      existingGoalNames: this.getExistingGoalNames(goal.goalId)
    }, goal.goalId?.toString());
  }

  removeGoal(goal: GoalGet): void {
    // Check if goalId exists
    if (!goal.goalId) {
      console.error('Goal ID is undefined, cannot delete goal');
      return;
    }

    // Show confirmation dialog
    this.dialogService.openConfirmDialog(
      'Delete Goal',
      `Are you sure you want to delete the goal "${goal.goalName}"? This action cannot be undone.`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed && goal.goalId) {
        // Call the delete endpoint
        this.dataService.deleteGoal(goal.goalId).subscribe({
          next: (success) => {
            if (success) {
              console.log('Goal deleted successfully');
            } else {
              console.error('Failed to delete goal');
            }
            // If the deleted goal is currently open, close the editor
            if (this.editorMode === 'goal' && this.highlightedItemId === goal.goalId!.toString()) {
              this.isEditorVisible = false;
              setTimeout(() => {
                this.editorMode = null;
                this.editorData = null;
                this.highlightedItemId = null;
                this.editorTitle = 'Editor';
                this.editorSubtitle = '';
              }, 200);
            }
            this.refreshProjectData();
          },
          error: (error) => {
            console.error('Error deleting goal:', error);
            // Refresh to ensure UI is in sync with server state
            this.refreshProjectData();
          }
        });
      }
      // Note: If user cancelled, we don't need to refresh as no server-side changes occurred
    });
  }

  onGoalDropped(event: CdkDragDrop<GoalGet[]>): void {
    if (event.previousIndex !== event.currentIndex) {
      moveItemInArray(this.projectGoals, event.previousIndex, event.currentIndex);

      const reorderItems = this.projectGoals
        .map((goal, index) => ({ goal, newOrder: index + 1 }))
        .filter(({ goal, newOrder }) => goal.order !== newOrder);

      if (reorderItems.length === 0) return;

      // Update local order values immediately
      reorderItems.forEach(({ goal, newOrder }) => goal.order = newOrder);

      const buildStepsFromTasks = (tasks: TaskGet[]) =>
        tasks.map(task => {
          // taskJson is the canonical stored representation: { task_id, params, select: { columns } }
          const raw = (task as any).taskJson;
          if (raw) {
            try { return JSON.parse(raw); } catch { /* fall through to field-based fallback */ }
          }
          // Fallback: reconstruct from individual fields if taskJson is unavailable
          let params: any = {};
          try { params = task.parameters ? JSON.parse(task.parameters as string) : {}; } catch { params = task.parameters || {}; }
          return {
            task_id: task.taskId,
            params,
            select: { columns: task.targetColumn ? [task.targetColumn] : [] }
          };
        });

      const updates = reorderItems.map(({ goal, newOrder }) => {
        const buildUpdate = (tasks: TaskGet[]) => {
          const payload: any = {
            GoalId: goal.goalId,
            GoalCatalogId: goal.goalCatalogId,
            GoalName: goal.goalName,
            Description: goal.description,
            IsActive: goal.isActive,
            Order: newOrder,
            Steps: buildStepsFromTasks(tasks)
          };
          return this.dataService.updateGoal(payload);
        };

        const cached = this.getTasksForGoal(goal);
        if (cached.length > 0) {
          return buildUpdate(cached);
        }
        // Tasks not yet in cache — fetch first, then update
        return this.dataService.getTaskList(goal.goalId!).pipe(
          switchMap(tasks => buildUpdate(tasks))
        );
      });

      forkJoin(updates).subscribe({
        error: (error) => console.error('Error updating goal order:', error)
      });
    }
  }

  // Task methods
  // Goal-specific task methods
  getTasksForGoal(goal: GoalGet): TaskGet[] {
    // Return tasks for the given goal from the projectTasks array
    if (!goal || !goal.goalId) return [];
    return this.projectTasks.filter(task => String(task.goalId) === String(goal.goalId));
  }

  removeTask(task: Task): void {
    this.tasks = this.tasks.filter(t => t.id !== task.id);
  }

  // Output/Destination methods
  addOutput(): void {
    if (!this.projectId) {
      console.error('Cannot add output: Project ID is not available');
      return;
    }

    const datasourceId = this.projectDataSource?.dataSourceId || this.project?.dataSourceId;
    
    this.hasPendingDestination = true;
    this.activateSidebarEditor('destination', {
      title: 'Set Output',
      projectId: this.projectId,
      datasourceId: datasourceId,
      mode: 'create',
      schemaData: this.getSchemaDataForDialog(),
      existingDestinations: this.projectDataDestinations
    });
  }

  editDestination(destination: DataDestinationGet): void {
    console.log('Editing output:', destination);
    
    if (!this.projectId) {
      console.error('Cannot edit output: Project ID is not available');
      return;
    }

    const datasourceId = this.projectDataSource?.dataSourceId || this.project?.dataSourceId;
    
    this.activateSidebarEditor('destination', {
      title: 'View Output',
      projectId: this.projectId,
      datasourceId: datasourceId,
      mode: 'view',
      destination: destination,
      schemaData: this.getSchemaDataForDialog()
    }, destination.dataDestinationId?.toString());
  }

  getDestinationDescription(destination: DataDestinationGet): string {
    // Try to parse settings JSON for more details
    if (destination.settingsJson) {
      try {
        const settings = JSON.parse(destination.settingsJson);
        // Build description based on destination type
        const parts: string[] = [];
        
        if (settings.connectionString) {
          parts.push('Database connection configured');
        }
        if (settings.filePath) {
          parts.push(`Path: ${settings.filePath}`);
        }
        if (settings.tableName) {
          parts.push(`Table: ${settings.tableName}`);
        }
        if (settings.apiEndpoint) {
          parts.push(`Endpoint: ${settings.apiEndpoint}`);
        }
        
        if (parts.length > 0) {
          return parts.join(' • ');
        }
      } catch (e) {
        console.warn('Failed to parse destination settings JSON:', e);
      }
    }
    
    // Fallback description
    return `${destination.destinationTypeName} output`;
  }

  removeOutput(destination: DataDestinationGet): void {
    // Check if dataDestinationId exists
    if (!destination.dataDestinationId) {
      console.error('Output ID is undefined, cannot delete output');
      return;
    }

    // Show confirmation dialog
    this.dialogService.openConfirmDialog(
      'Delete Output',
      `Are you sure you want to delete this output? This action cannot be undone.`,
      'Delete',
      'Cancel'
    ).subscribe(confirmed => {
      if (confirmed && destination.dataDestinationId) {
        // Call the delete endpoint
        this.dataService.deleteDataDestination(destination.dataDestinationId).subscribe({
          next: (success) => {
            if (success) {
              console.log('Output deleted successfully');
              // After deletion, refresh destinations. If this was the last destination,
              // show the post-goal help instead of the post-destination help.
              this.redirectToAfterGoalIfNoDestinations = true;
              this.refreshProjectData({ destinations: true });
            } else {
              console.error('Failed to delete data Output');
              // Refresh anyway to sync with server state
              this.refreshProjectData({ destinations: true });
            }
          },
          error: (error) => {
            console.error('Error deleting data Output:', error);
            // Refresh to ensure UI is in sync with server state
            this.refreshProjectData({ destinations: true });
          }
        });
      }
    });
  }

  // Cleaning Metrics Methods
  processCleaningMetrics(): void {
    // Build goals map from projectGoals
    this.goalsMap.clear();
    this.projectGoals.forEach(goal => {
      if (goal.goalId) {
        this.goalsMap.set(goal.goalId, goal.goalName);
      }
    });

    // Extract audit entries from response data
    if (this.metricsResultData?.audit && Array.isArray(this.metricsResultData.audit)) {
      this.auditEntries = this.metricsResultData.audit;
      
      // Calculate total duration
      this.totalDuration = this.auditEntries.reduce((sum, entry) => sum + entry.duration_ms, 0);
      
      // Group by goal
      const goalGroups = new Map<string, AuditEntry[]>();
      this.auditEntries.forEach(entry => {
        if (!goalGroups.has(entry.goal_id)) {
          goalGroups.set(entry.goal_id, []);
        }
        goalGroups.get(entry.goal_id)!.push(entry);
      });
      
      // Create goal metrics
      this.goalMetrics = Array.from(goalGroups.entries()).map(([goalId, steps]) => {
        const totalDuration = steps.reduce((sum, step) => sum + step.duration_ms, 0);
        return {
          goalId,
          goalName: this.goalsMap.get(goalId) || `Goal ${goalId.substring(0, 8)}`,
          totalDuration,
          steps
        };
      });
      
      // Sort goal metrics by step index (first step in each goal)
      this.goalMetrics.sort((a, b) => a.steps[0].step_index - b.steps[0].step_index);
      
      // Create timeline blocks with spacing
      const gapBetweenTasks = 0.8; // 0.8% gap between tasks within the same goal
      const gapBetweenGoals = 1.2; // 1.2% gap between different goals
      const minBlockWidthPercent = 0.8; // Minimum 0.8% width for clickability (roughly 8px on a 1000px container)
      
      // Calculate how many gaps and goal boundaries
      let regularGaps = 0;
      let goalBoundaryGaps = 0;
      
      for (let i = 0; i < this.auditEntries.length - 1; i++) {
        const currentGoal = this.auditEntries[i].goal_id;
        const nextGoal = this.auditEntries[i + 1].goal_id;
        
        if (currentGoal === nextGoal) {
          regularGaps++;
        } else {
          goalBoundaryGaps++;
        }
      }
      
      // Total space reserved for gaps
      const totalGapSpace = (regularGaps * gapBetweenTasks) + (goalBoundaryGaps * gapBetweenGoals);
      let availableSpace = 100 - totalGapSpace;
      
      // Calculate initial proportional widths
      const initialWidths = this.auditEntries.map(entry => 
        (entry.duration_ms / this.totalDuration) * availableSpace
      );
      
      // Count blocks that need minimum width adjustment
      const belowMinCount = initialWidths.filter(w => w < minBlockWidthPercent).length;
      
      if (belowMinCount > 0) {
        // Reserve space for minimum widths
        const reservedSpace = belowMinCount * minBlockWidthPercent;
        
        // Check if we have enough space after reserving minimums
        if (reservedSpace < availableSpace) {
          // Distribute remaining space proportionally among blocks
          const remainingSpace = availableSpace - reservedSpace;
          const totalDurationAboveMin = this.auditEntries
            .filter((_, i) => initialWidths[i] >= minBlockWidthPercent)
            .reduce((sum, entry) => sum + entry.duration_ms, 0);
          
          // Recalculate widths with minimum constraint
          initialWidths.forEach((width, i) => {
            if (width < minBlockWidthPercent) {
              initialWidths[i] = minBlockWidthPercent;
            } else if (totalDurationAboveMin > 0) {
              initialWidths[i] = (this.auditEntries[i].duration_ms / totalDurationAboveMin) * remainingSpace;
            }
          });
        } else {
          // If we don't have enough space, scale everything down proportionally
          const scaleFactor = availableSpace / (this.auditEntries.length * minBlockWidthPercent);
          initialWidths.forEach((_, i) => {
            initialWidths[i] = minBlockWidthPercent * scaleFactor;
          });
        }
      }
      
      // Create timeline blocks with calculated widths and gaps
      let cumulativePercent = 0;
      this.timelineBlocks = this.auditEntries.map((entry, index) => {
        const widthPercent = initialWidths[index];
        const leftPercent = cumulativePercent;
        
        // Move cumulative position forward by this block's width
        cumulativePercent += widthPercent;
        
        // Add gap after this block (unless it's the last one)
        if (index < this.auditEntries.length - 1) {
          const currentGoal = entry.goal_id;
          const nextGoal = this.auditEntries[index + 1].goal_id;
          
          if (currentGoal === nextGoal) {
            cumulativePercent += gapBetweenTasks;
          } else {
            cumulativePercent += gapBetweenGoals;
          }
        }
        
        return {
          entry,
          widthPercent,
          leftPercent
        };
      });
      
      // Select the first step by default
      if (this.auditEntries.length > 0) {
        this.selectedBlock = this.auditEntries[0];
      }
    }
  }

  getGoalBracePosition(goalMetric: GoalMetrics): { left: number; width: number } {
    const firstStep = goalMetric.steps[0];
    const lastStep = goalMetric.steps[goalMetric.steps.length - 1];
    
    // Find the timeline blocks that correspond to this goal's first and last steps
    const firstBlock = this.timelineBlocks.find(block => block.entry.step_index === firstStep.step_index);
    const lastBlock = this.timelineBlocks.find(block => block.entry.step_index === lastStep.step_index);
    
    if (!firstBlock || !lastBlock) {
      // Fallback to old calculation if blocks not found
      return { left: 0, width: 0 };
    }
    
    const left = firstBlock.leftPercent;
    const width = (lastBlock.leftPercent + lastBlock.widthPercent) - firstBlock.leftPercent;
    
    return { left, width };
  }

  formatDuration(ms: number): string {
    if (ms < 1000) {
      return `${ms}ms`;
    } else if (ms < 60000) {
      return `${(ms / 1000).toFixed(2)}s`;
    } else {
      const minutes = Math.floor(ms / 60000);
      const seconds = ((ms % 60000) / 1000).toFixed(0);
      return `${minutes}m ${seconds}s`;
    }
  }

  getRowsAffectedPercentage(entry: AuditEntry): string {
    if (entry.rows_affected === null || entry.input_rows === 0) {
      return '0';
    }
    return ((entry.rows_affected / entry.input_rows) * 100).toFixed(1);
  }

  getBlockTooltip(entry: AuditEntry): string {
    const status = entry.status ? 'Success' : 'Failed';
    const duration = this.formatDuration(entry.duration_ms);
    const rows = entry.rows_affected !== null ? entry.rows_affected : 0;
    const sourceColumn = entry.inputs?.source ? `\nSource Column: ${entry.inputs.source}` : '';
    return `${entry.task_id}${sourceColumn}\nStatus: ${status}\nDuration: ${duration}\nRows Affected: ${rows} / ${entry.input_rows}`;
  }

  selectBlock(entry: AuditEntry): void {
    // Always select the clicked block (no toggle behavior)
    this.selectedBlock = entry;
  }

  clearSelection(): void {
    this.selectedBlock = null;
  }

  getFullRunMetrics(): { totalDuration: number; totalRows: number; totalRowsAffected: number; successCount: number; failedCount: number } {
    const totalRows = this.auditEntries.reduce((sum, entry) => sum + entry.input_rows, 0);
    const totalRowsAffected = this.auditEntries.reduce((sum, entry) => sum + (entry.rows_affected || 0), 0);
    const successCount = this.auditEntries.filter(entry => entry.status).length;
    const failedCount = this.auditEntries.filter(entry => !entry.status).length;
    
    return {
      totalDuration: this.totalDuration,
      totalRows,
      totalRowsAffected,
      successCount,
      failedCount
    };
  }

  getTotalTaskCount(): number {
    return this.projectTasks.length;
  }
}
